import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Plus, Trash, Play, Pause, ArrowClockwise, CalendarCheck, Student, Heartbeat, CheckCircle, X } from "@phosphor-icons/react";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { autoPrefixUS } from "./PhoneInput";

const DAYS = [
  { code: "mon", label: "M" },
  { code: "tue", label: "T" },
  { code: "wed", label: "W" },
  { code: "thu", label: "T" },
  { code: "fri", label: "F" },
  { code: "sat", label: "S" },
  { code: "sun", label: "S" },
];

const PRESETS = [
  { name: "Dialysis MWF", trip_type: "dialysis", days: ["mon", "wed", "fri"] },
  { name: "Dialysis TThS", trip_type: "dialysis", days: ["tue", "thu", "sat"] },
  { name: "School M–F",  trip_type: "school",   days: ["mon", "tue", "wed", "thu", "fri"] },
  { name: "Wound care",  trip_type: "appointment", days: ["tue", "thu"] },
];

const emptyForm = {
  label: "", facility_name: "", contact_name: "", contact_phone: "", contact_email: "",
  patient_name: "", mobility_type: "wheelchair", trip_type: "dialysis",
  pickup_address: "", dropoff_address: "", pickup_time: "07:00",
  return_trip: true, notes: "", start_date: "", end_date: "",
  days_of_week: ["mon", "wed", "fri"], skip_dates: [],
};

export default function RecurringSeries() {
  const [series, setSeries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/recurring");
      setSeries(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load series");
    }
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (code) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(code) ? f.days_of_week.filter((d) => d !== code) : [...f.days_of_week, code],
    }));
  };

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, label: p.name, trip_type: p.trip_type, days_of_week: p.days }));
  };

  const save = async () => {
    if (!form.label || !form.start_date || !form.end_date || form.days_of_week.length === 0) {
      toast.error("Fill in label, start/end dates, and at least one weekday");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/recurring", form);
      toast.success("Series created");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create series");
    } finally { setSaving(false); }
  };

  const generate = async (id) => {
    try {
      const { data } = await apiClient.post(`/recurring/${id}/generate`, {});
      toast.success(`Generated ${data.created} bookings through ${data.generated_through || "n/a"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Generation failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await apiClient.patch(`/recurring/${id}/status`, null, { params: { status } });
      load();
    } catch (e) { toast.error("Status update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this series? Existing bookings stay.")) return;
    try {
      await apiClient.delete(`/recurring/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="recurring-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold text-ink">Recurring series</h2>
        <button onClick={() => setOpen(true)} data-testid="recurring-new-btn" className="inline-flex items-center gap-2 bg-brandblue hover:bg-brandblue-hover text-white text-xs font-semibold px-3 py-2 rounded-md">
          <Plus size={12} weight="bold" /> New series
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {PRESETS.map((p) => (
          <button
            key={p.name}
            onClick={() => { setForm({ ...emptyForm, label: p.name, trip_type: p.trip_type, days_of_week: p.days }); setOpen(true); }}
            className="text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-brandblue hover:shadow-sm transition"
          >
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-brandblue">
              {p.trip_type === "school" ? <Student size={12} /> : <Heartbeat size={12} />}
              {p.trip_type}
            </div>
            <div className="font-heading text-sm font-bold mt-1.5">{p.name}</div>
            <div className="mt-1 flex gap-1">
              {DAYS.map((d) => (
                <span key={d.code} className={`h-5 w-5 grid place-items-center rounded text-[9px] font-bold ${p.days.includes(d.code) ? "bg-brandblue text-white" : "bg-slate-100 text-slate-400"}`}>
                  {d.label}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>

      {series.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No series yet. Start from a preset above or click <b>New series</b>.
        </div>
      ) : (
        <div className="space-y-3">
          {series.map((s) => (
            <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`recurring-card-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="inline-flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-brandblue">{s.trip_type}</span>
                    <StatusPill status={s.status} />
                  </div>
                  <div className="font-heading text-lg font-bold text-ink mt-1">{s.label}</div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    {s.patient_name || "—"} · {s.mobility_type} · {s.pickup_time}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate max-w-xl">
                    {s.pickup_address} → {s.dropoff_address}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {DAYS.map((d) => (
                    <span key={d.code} className={`h-7 w-7 grid place-items-center rounded text-[10px] font-bold ${s.days_of_week?.includes(d.code) ? "bg-brandblue text-white" : "bg-slate-100 text-slate-400"}`}>
                      {d.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-[11px] text-slate-500">
                  {s.start_date} → {s.end_date} · generated through: <span className="font-mono text-slate-700">{s.generated_through || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => generate(s.id)} data-testid={`recurring-generate-${s.id}`} className="inline-flex items-center gap-1.5 bg-brandblue hover:bg-brandblue-hover text-white text-xs font-semibold px-3 py-1.5 rounded-md">
                    <ArrowClockwise size={12} /> Generate 14d
                  </button>
                  {s.status === "active" ? (
                    <button onClick={() => setStatus(s.id, "paused")} className="inline-flex items-center gap-1.5 border border-slate-300 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-slate-50">
                      <Pause size={12} /> Pause
                    </button>
                  ) : (
                    <button onClick={() => setStatus(s.id, "active")} className="inline-flex items-center gap-1.5 border border-slate-300 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-slate-50">
                      <Play size={12} /> Resume
                    </button>
                  )}
                  <button onClick={() => remove(s.id)} className="h-8 w-8 grid place-items-center border border-red-200 text-red-500 hover:bg-red-50 rounded-md">
                    <Trash size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Series Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
            onClick={(e) => e.target === e.currentTarget && setOpen(false)}
          >
            <motion.div
              initial={{ y: 24, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 24, scale: 0.98 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              data-testid="recurring-drawer"
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue">New recurring series</div>
                  <h3 className="font-heading text-lg font-bold text-ink">Configure the pattern</h3>
                </div>
                <button onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded hover:bg-slate-100"><X size={14} /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <F label="Label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} placeholder="Jane Doe — Dialysis MWF" testid="rec-label" />
                  <F label="Trip type">
                    <select value={form.trip_type} onChange={(e) => setForm({ ...form, trip_type: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                      {["dialysis", "appointment", "school", "facility_to_facility", "other"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </F>
                  <F label="Patient name" value={form.patient_name} onChange={(v) => setForm({ ...form, patient_name: v })} testid="rec-patient" />
                  <F label="Mobility">
                    <select value={form.mobility_type} onChange={(e) => setForm({ ...form, mobility_type: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                      {["ambulatory", "wheelchair", "stretcher"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </F>
                  <F label="Facility" value={form.facility_name} onChange={(v) => setForm({ ...form, facility_name: v })} testid="rec-facility" />
                  <F label="Contact name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="rec-contact" />
                  <F label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })}
                     onBlur={() => setForm((f) => ({ ...f, contact_phone: autoPrefixUS(f.contact_phone) }))} />
                  <F label="Contact email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} type="email" />
                  <F label="Pickup address" value={form.pickup_address} onChange={(v) => setForm({ ...form, pickup_address: v })} testid="rec-pickup" />
                  <F label="Drop-off address" value={form.dropoff_address} onChange={(v) => setForm({ ...form, dropoff_address: v })} testid="rec-dropoff" />
                  <F label="Pickup time" value={form.pickup_time} onChange={(v) => setForm({ ...form, pickup_time: v })} type="time" />
                  <F label="Return trip">
                    <label className="mt-2 inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.return_trip} onChange={(e) => setForm({ ...form, return_trip: e.target.checked })} />
                      Include return
                    </label>
                  </F>
                  <F label="Start date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} type="date" testid="rec-start" />
                  <F label="End date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} type="date" testid="rec-end" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">Days of week</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {DAYS.map((d) => (
                      <button
                        key={d.code}
                        type="button"
                        onClick={() => toggleDay(d.code)}
                        className={`h-9 w-9 rounded-md text-xs font-bold ${form.days_of_week.includes(d.code) ? "bg-brandblue text-white" : "bg-slate-100 text-slate-600 border border-slate-200"}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
                <F label="Notes">
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                </F>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3 sticky bottom-0 bg-white">
                <button onClick={() => setOpen(false)} className="text-sm text-slate-600 hover:text-ink">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="recurring-save" className="inline-flex items-center gap-2 bg-brandblue hover:bg-brandblue-hover text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
                  <CalendarCheck size={14} weight="fill" /> {saving ? "Saving…" : "Create series"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function F({ label, children, value, onChange, type = "text", placeholder, testid, onBlur }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">{label}</label>
      {children ?? (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          data-testid={testid}
          className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = {
    active: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
    ended: "bg-slate-100 text-slate-600",
  }[status] || "bg-slate-100 text-slate-600";
  return <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded ${tone}`}>{status}</span>;
}
